/**
 * E06-F05-T16 — manual check of a company the AI check could not read.
 *
 * Large companies block the scraper: the row ends as "Failed — open it manually to
 * check". This drawer is where the person does exactly that: an "Open website" link,
 * then a verdict (Matches / Partly / Does not match) per scored criterion with an
 * optional note and the page they read it on. Save is FREE (no AI call): the server
 * scores the verdicts with the same weighted math as the AI check and marks the row
 * "Checked manually". DOM-free rules live in `researchAiChainModel.ts`.
 */
import { useState, type JSX } from 'react';
import type {
  ManualCheckCriterion,
  ManualCheckInput,
  ManualCheckVerdict,
  PipelineRow,
} from '@eer/shared';
import { Drawer } from '../ui/index.js';
import { companyAriaLabel } from './CompanyLabel.js';
import {
  MANUAL_CHECK_FIELDS,
  MANUAL_VERDICT_OPTIONS,
  manualCheckBody,
  manualCheckDraftFrom,
  missingVerdicts,
  type ManualCheckDraft,
} from './researchAiChainModel.js';

export interface ManualCheckDrawerProps {
  readonly row: PipelineRow;
  readonly saving: boolean;
  readonly onSave: (body: ManualCheckInput) => void;
  readonly onClose: () => void;
}

export function ManualCheckDrawer({
  row,
  saving,
  onSave,
  onClose,
}: ManualCheckDrawerProps): JSX.Element {
  const [draft, setDraft] = useState<ManualCheckDraft>(() => manualCheckDraftFrom(row));
  const missing = missingVerdicts(draft);
  const body = manualCheckBody(draft);
  const website = row.domain ? `https://${row.domain}` : null;

  function setVerdict(key: ManualCheckCriterion, verdict: ManualCheckVerdict): void {
    setDraft((d) => ({ ...d, verdicts: { ...d.verdicts, [key]: verdict } }));
  }
  function setNote(key: ManualCheckCriterion, note: string): void {
    setDraft((d) => ({ ...d, notes: { ...d.notes, [key]: note } }));
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={`Manual check — ${companyAriaLabel(row.name)}`}
      width="lg"
      testId="aichain-manual-drawer"
      footer={
        <div className="flex items-center justify-end gap-3">
          {missing.length > 0 ? (
            <span data-testid="aichain-manual-missing" className="text-xs text-amber-700">
              Set a verdict for every criterion ({missing.length} left).
            </span>
          ) : null}
          <button
            type="button"
            data-testid="aichain-manual-cancel"
            onClick={onClose}
            className="rounded border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="aichain-manual-save"
            disabled={body == null || saving}
            onClick={() => body && onSave(body)}
            className="rounded bg-[#D4AF37] px-4 py-1.5 text-sm font-bold text-slate-900 shadow hover:bg-[#B5952F] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
          >
            Save manual check
          </button>
        </div>
      }
    >
      <div className="space-y-4" data-testid="aichain-manual-body">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          The website blocked the automatic check. Open it in a new tab, look through it, and set
          what you see for each criterion. Saving is free — the score is calculated the same way as
          for the AI check, and the company is marked <b>Checked manually</b>.
          {website ? (
            <a
              href={website}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="aichain-manual-open-site"
              className="mt-2 block font-semibold text-blue-700 hover:underline"
            >
              <i className="fa-solid fa-arrow-up-right-from-square mr-1" aria-hidden />
              Open {row.domain}
            </a>
          ) : (
            <span className="mt-2 block text-xs">
              Website not found — enter it in Review first.
            </span>
          )}
        </div>

        {MANUAL_CHECK_FIELDS.map((field) => (
          <fieldset
            key={field.key}
            data-testid={`aichain-manual-field-${field.key}`}
            className="rounded-lg border border-border px-3 py-2"
          >
            <legend className="px-1 text-sm font-semibold text-slate-800">{field.label}</legend>
            <p className="mb-1 text-xs text-slate-500">{field.hint}</p>
            <div className="flex flex-wrap gap-3">
              {MANUAL_VERDICT_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-1 text-sm text-slate-700">
                  <input
                    type="radio"
                    name={`manual-${field.key}`}
                    data-testid={`aichain-manual-${field.key}-${opt.value}`}
                    checked={draft.verdicts[field.key] === opt.value}
                    onChange={() => setVerdict(field.key, opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            <input
              type="text"
              data-testid={`aichain-manual-note-${field.key}`}
              aria-label={`${field.label} — what you saw`}
              placeholder="What you saw (optional)"
              maxLength={1000}
              value={draft.notes[field.key]}
              onChange={(e) => setNote(field.key, e.target.value)}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </fieldset>
        ))}

        <label className="block text-sm text-slate-700">
          Page you checked
          <input
            type="text"
            data-testid="aichain-manual-source"
            maxLength={500}
            value={draft.sourceUrl}
            onChange={(e) => setDraft((d) => ({ ...d, sourceUrl: e.target.value }))}
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
        </label>
        <label className="block text-sm text-slate-700">
          Comment (optional)
          <textarea
            data-testid="aichain-manual-comment"
            maxLength={1000}
            rows={2}
            value={draft.comment}
            onChange={(e) => setDraft((d) => ({ ...d, comment: e.target.value }))}
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
        </label>
      </div>
    </Drawer>
  );
}

export default ManualCheckDrawer;
